/* Secondary actions for a list row, as one bottom sheet.

   A row's primary action is the row itself; anything else is disclosed here. The
   caller hands over what is being acted on and a list of actions, and gets back
   the id of the one chosen.

   The sheet is the confirmation. It names its subject and states what each
   action does before the tap, so an action that needs no further input runs
   straight away. An action that needs input — Settings' delete has to ask where
   the items go — escalates to `g-dialog` afterwards.

   Four mechanics are not obvious from the markup:

   1. The host is `position: fixed` with its bottom raised by `--overhang`. In
      the installed PWA the layout viewport runs past what you can see by the
      system navigation bar's height, so an `inset: 0` overlay would put the last
      action underneath it. Shortening the host rather than padding the content
      also keeps `g-sheet`'s measurements in visible pixels.
      `env(safe-area-inset-bottom)` cannot stand in: it reports 0 there, because
      a nav bar is not a display cutout.
   2. An empty ladder makes every state modal, so the only gesture `g-sheet`
      offers is a downward dismiss.
   3. The exit is owned here, not by the caller: `dismiss` fires once the sheet
      has finished travelling down, so a caller that unmounts on that event keeps
      the content on screen for the whole of it.
   4. Choosing an action drops the history entry in place; only a user dismissal
      traverses. A caller that opens something else on `act` would otherwise race
      a pop still in flight (ADR 0019). */

import { LitElement, html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { GESTURE_ATTR } from '../../core/gestures.js';
import '../g-sheet/g-sheet.js';
import '../g-icon/g-icon.js';

export interface SheetAction {
  id: string;
  label: string;
  /** g-icon name. */
  icon: string;
  /** What the action does, in one line. */
  note?: string;
  tone?: 'danger';
}

/** Sized by its content: a subject and two actions is not a fraction of a screen. */
const DETENTS = { open: 'content' } as const;
const BOUNDS = { min: 0.15, max: 0.66 };
const LADDER: string[] = [];

/** Matches g-sheet's transition, after which the sheet is off screen. */
const EXIT_MS = 300;

@customElement('g-action-sheet')
export class GActionSheet extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: var(--overhang, 0px);
        z-index: 40;
        font-family: var(--font-ui);
      }
      .scrim {
        position: absolute;
        inset: 0;
        background: rgb(0 0 0 / 0.45);
        opacity: 0;
        transition: opacity 300ms ease;
      }
      :host([open]) .scrim {
        opacity: 1;
      }
      /* The document-level reduced-motion rule cannot cross this shadow root. */
      @media (prefers-reduced-motion: reduce) {
        .scrim {
          transition: none;
        }
      }
      /* The sheet's handle strip supplies the space above; this pays for the
         bottom, so the last action is not flush with the screen edge. */
      .view {
        padding-bottom: var(--space-3);
      }
      .subject {
        padding: 0 var(--space-4) var(--space-3);
      }
      .name {
        font-family: var(--font-display);
        font-size: 1.05rem;
        font-weight: 700;
        overflow-wrap: anywhere;
      }
      .meta {
        margin-top: 2px;
        color: var(--text-dim);
        font-size: 0.82rem;
      }
      .act {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        width: 100%;
        min-height: var(--touch-min);
        padding: var(--space-3) var(--space-4);
        border: 0;
        border-top: 1px solid var(--border);
        background: none;
        color: var(--text);
        font: inherit;
        font-family: var(--font-ui);
        text-align: left;
        cursor: pointer;
        --icon-size: 20px;
      }
      .act:active {
        background: var(--surface-2);
      }
      .act:focus-visible {
        outline: none;
        box-shadow: var(--ring);
      }
      .act g-icon {
        flex: none;
        margin-top: 1px;
        color: var(--text-dim);
      }
      .label {
        display: block;
        font-size: 0.95rem;
      }
      .note {
        display: block;
        margin-top: 2px;
        color: var(--text-dim);
        font-size: 0.82rem;
        line-height: 1.4;
      }
      /* Distance is what keeps a destructive action away from a safe one; a
         hairline between adjacent rows is not distance. */
      .act.danger {
        margin-top: var(--space-3);
      }
      .act.danger:active {
        color: var(--danger);
        background: color-mix(in oklab, var(--danger) 12%, transparent);
      }
      .act.danger:active g-icon {
        color: var(--danger);
      }
    `,
  ];

  /** What is being acted on. */
  @property() heading = '';
  /** A line under the heading — kind, counts, whatever identifies it. */
  @property() meta = '';
  @property({ attribute: false }) actions: SheetAction[] = [];

  /** Drives both the sheet's detent and the scrim's fade. */
  @property({ type: Boolean, reflect: true }) open = false;

  @query('.act') private firstAct?: HTMLButtonElement;

  /** True while this instance owns a history entry. */
  private entry = false;
  private closing = false;
  private exit?: ReturnType<typeof setTimeout>;

  override connectedCallback(): void {
    super.connectedCallback();
    // On the host rather than on the sheet, so the scrim is covered by the same
    // declaration: a drag anywhere over a modal overlay belongs to the overlay.
    this.setAttribute(GESTURE_ATTR, '');
    window.addEventListener('popstate', this.onPopState);
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('resize', this.measureOverhang);
    this.measureOverhang();
    // Deliberately not spread — see ADR 0021 decision 2. Only Index and Settings
    // raise this sheet, so there is no `overlay` below it to preserve.
    history.pushState({ actionSheet: true }, '');
    this.entry = true;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('resize', this.measureOverhang);
    clearTimeout(this.exit);
    // Unmounted without ever being dismissed — the workspace went away
    // underneath it. The entry still has to go, or the next Back does nothing.
    this.dropEntry();
  }

  override firstUpdated(): void {
    // A frame later, so the sheet has measured itself and has somewhere to
    // travel from; setting the detent in the same frame skips the animation.
    requestAnimationFrame(() => {
      this.open = true;
      void this.updateComplete.then(() => this.firstAct?.focus());
    });
  }

  /**
   * How much of a fixed overlay falls past the visible viewport.
   *
   * The gap between the layout viewport and the document's own box is the system
   * navigation bar in an installed PWA, and 0 everywhere else.
   */
  private measureOverhang = (): void => {
    const over = Math.max(0, window.innerHeight - document.body.clientHeight);
    this.style.setProperty('--overhang', `${over}px`);
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.dismiss();
  };

  /** Back closes the sheet; the entry it popped was ours, so nothing else owes one. */
  private onPopState = (): void => {
    if (!this.entry) return;
    this.entry = false;
    this.close();
  };

  /** Give up the entry without traversing, for a close that is followed by something else. */
  private dropEntry(): void {
    if (!this.entry) return;
    this.entry = false;
    const state = { ...((history.state as Record<string, unknown> | null) ?? {}) };
    if (state.actionSheet) {
      delete state.actionSheet;
      history.replaceState(state, '');
    }
  }

  /** Scrim, drag-down, Escape: the sheet is abandoned, so its entry goes with it. */
  private dismiss(): void {
    if (this.entry) {
      this.entry = false;
      if ((history.state as { actionSheet?: boolean } | null)?.actionSheet) history.back();
    }
    this.close();
  }

  /** Travel down, then tell the caller it is safe to unmount. */
  private close(): void {
    if (this.closing) return;
    this.closing = true;
    this.open = false;
    this.exit = setTimeout(
      () => this.dispatchEvent(new CustomEvent('dismiss', { bubbles: true, composed: true })),
      EXIT_MS,
    );
  }

  private act(id: string): void {
    this.dropEntry();
    this.close();
    this.dispatchEvent(new CustomEvent('act', { detail: id, bubbles: true, composed: true }));
  }

  override render() {
    return html`
      <div class="scrim" @click=${() => this.dismiss()}></div>
      <g-sheet
        .state=${this.open ? 'open' : ''}
        .detents=${DETENTS}
        .bounds=${BOUNDS}
        .ladder=${LADDER}
        @sheet-state=${() => this.dismiss()}
      >
        <div class="view" role="dialog" aria-modal="true" aria-label=${this.heading}>
          <div class="subject">
            <div class="name">${this.heading}</div>
            ${this.meta ? html`<div class="meta">${this.meta}</div>` : ''}
          </div>
          ${this.actions.map(
            (a) => html`
              <button class="act ${a.tone ?? ''}" @click=${() => this.act(a.id)}>
                <g-icon name=${a.icon}></g-icon>
                <span>
                  <span class="label">${a.label}</span>
                  ${a.note ? html`<span class="note">${a.note}</span>` : ''}
                </span>
              </button>
            `,
          )}
        </div>
      </g-sheet>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-action-sheet': GActionSheet;
  }
}
